import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import Carousel from "../carousel/carousel";

interface ImageTapeProps {
  items: string[];
  height?: number;
  width?: number;
  /** Drift speed of the auto-scroll, in pixels per second. */
  speed?: number;
  /** Aspect ratio used by the phone carousel. Match the artwork so the
   *  before/after pair is shown whole rather than cropped. */
  ratio?: string;
}

/** Before / after gallery. A drifting photo strip on tablet and up; on a phone
 *  it becomes a carousel, which keeps the page free of sideways scrolling. */
const ImageTape: React.FC<ImageTapeProps> = ({
  items,
  height = 300,
  width = 240,
  speed = 40,
  ratio = "2 / 1",
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [isPlaying, setIsPlaying] = useState(true);

  /* Phones get a carousel instead of a strip: one photo at a time inside a
     clipping frame, so nothing on the page can be dragged sideways. It also
     shows each before/after pair whole — the strip has to crop these wide
     images hard to fit a phone-sized column. */
  const isCarousel = isSmallScreen;

  /* The strip is rendered twice so the drift can wrap without a visible jump. */
  const canLoop = items.length > 1 && !isCarousel;
  const loopItems = canLoop ? [...items, ...items] : items;

  /** Distance covered by one full set of photos — the wrap point. */
  const periodRef = useRef(0);
  /** Fractional scroll position; scrollLeft alone is too coarse to drift on. */
  const positionRef = useRef(0);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    const first = el?.children[0] as HTMLElement | undefined;
    const second = el?.children[items.length] as HTMLElement | undefined;
    periodRef.current = canLoop && first && second ? second.offsetLeft - first.offsetLeft : 0;
  }, [items.length, canLoop]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, isSmallScreen]);

  useEffect(() => {
    if (prefersReducedMotion) setIsPlaying(false);
  }, [prefersReducedMotion]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isPlaying || !canLoop) return;

    positionRef.current = el.scrollLeft;
    let frame = 0;
    let last: number | null = null;

    const step = (now: number) => {
      if (!periodRef.current) measure();
      if (last !== null) {
        positionRef.current += (speed * (now - last)) / 1000;
        const period = periodRef.current;
        if (period > 0 && positionRef.current >= period) positionRef.current -= period;
        el.scrollLeft = positionRef.current;
      }
      last = now;
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying, canLoop, speed, measure]);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    /* Taking manual control stops the drift so the two don't fight. */
    setIsPlaying(false);
    const amount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const arrowSx = {
    position: "absolute" as const,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    width: 44,
    height: 44,
    color: "#2e5041",
    backgroundColor: "rgba(255,255,255,0.86)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 8px 20px rgba(43, 42, 39, 0.14)",
    transition: "transform 240ms cubic-bezier(0.34, 1.4, 0.64, 1), background-color 240ms ease",
    "&:hover": {
      backgroundColor: "#fff",
      transform: "translateY(-50%) scale(1.08)",
    },
  };

  if (isCarousel) {
    return (
      <Box sx={{ padding: "1.5rem 1rem 0" }}>
        <Carousel
          items={items}
          ratio={ratio}
          maxWidth="30rem"
          interval={speed > 0 ? 5000 : 0}
          label={(index) => `Before and after ${index + 1}`}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <IconButton onClick={() => scroll("left")} sx={{ ...arrowSx, left: 12 }} aria-label="Scroll left">
        <ArrowBackIosNew sx={{ fontSize: 16 }} />
      </IconButton>

      <Box
        ref={scrollRef}
        onClick={() => setIsPlaying((playing) => !playing)}
        role="button"
        tabIndex={0}
        aria-pressed={!isPlaying}
        aria-label={isPlaying ? "Pause the photo strip" : "Resume the photo strip"}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsPlaying((playing) => !playing);
          }
        }}
        sx={{
          display: "flex",
          gap: "12px",
          overflowX: "auto",
          /* A swipe that runs past the end must stop here, not pan the page. */
          overscrollBehaviorX: "contain",
          cursor: "pointer",
          /* Snapping would fight the drift, so it only applies once paused. */
          scrollSnapType: isPlaying ? "none" : "x proximity",
          padding: "1.5rem 12px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {loopItems.map((item, index) => {
          const isClone = index >= items.length;
          return (
            <Box
              key={index}
              aria-hidden={isClone}
              sx={{
                height,
                minWidth: width,
                flexShrink: 0,
                overflow: "hidden",
                borderRadius: "18px",
                scrollSnapAlign: "center",
                boxShadow: "0 12px 34px rgba(58, 48, 38, 0.07)",
                transition: "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)",
                "&:hover": { transform: "translateY(-6px)" },
                "&:hover img": { transform: "scale(1.06)" },
              }}
            >
              <img
                src={item}
                alt={isClone ? "" : `Before and after ${index + 1}`}
                loading="lazy"
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              />
            </Box>
          );
        })}
      </Box>

      <IconButton onClick={() => scroll("right")} sx={{ ...arrowSx, right: 12 }} aria-label="Scroll right">
        <ArrowForwardIos sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

export default ImageTape;
